import { Stack } from '@mantine/core';
import { MultiSelect } from '@repo/foundations';
import { useCallback, useMemo, useState } from 'react';

import { getSummaryAndBreadCrumbs } from '@repo/common/utils/breadcrumb';
import type { ISecodaEntity } from '../../api';
import { useSearch } from '../../api';
import { SecodaEntityIcon } from '../SecodaEntity/SecodaEntityIcon/SecodaEntityIcon';
import { DEFAULT_RESULT_LIMIT } from '../Spotlight/components/SearchPalette/constants';
import { getSearchResultActions } from '../Spotlight/components/SearchPalette/utils';
import type { AnnouncementFormKeys } from './types';

export interface AnnouncementRelatedResourceItem {
	value: string;
	label: string;
	entity: ISecodaEntity;
	breadcrumbText: string;
}

export function AnnouncementRelatedResource({
	onChange,
	value,
	setValue,
	selectedData,
	setSelectedData,
}: {
	onChange: (key: AnnouncementFormKeys) => (value: string | string[]) => void;
	value: string[];
	setValue: (value: string[]) => void;
	selectedData: AnnouncementRelatedResourceItem[];
	setSelectedData: (data: AnnouncementRelatedResourceItem[]) => void;
}) {
	const [searchTerm, setSearchTerm] = useState<string>('');

	const { data: searchResults } = useSearch({
		searchTerm: searchTerm,
		options: {
			select: ({ results }) => results,
		},
	});

	const searchResultActions = useMemo(
		() =>
			getSearchResultActions(
				searchResults,
				DEFAULT_RESULT_LIMIT,
				() => {},
				() => {}
			),
		[searchResults]
	);

	const searchData = useMemo<AnnouncementRelatedResourceItem[]>(
		() =>
			(searchResultActions ?? []).map((item) => ({
				...item,
				value: item.id,
				label: item.title,
				entity: item.entity as ISecodaEntity,
				breadcrumbText: getSummaryAndBreadCrumbs(
					item.entity?.native_type || item.entity?.entity_type || null,
					item.entity?.search_metadata
				),
			})),
		[searchResultActions]
	);

	const data = useMemo<AnnouncementRelatedResourceItem[]>(
		() => [...selectedData, ...searchData],
		[selectedData, searchData]
	);

	const controlledSetValue = useCallback(
		(newValue: string[]) => {
			const addedItems = newValue.filter((id) => !value.includes(id));

			onChange('related_resources')(newValue);
			setValue([...newValue]);

			// After adding a new item, the search term is cleared, so the new item is no longer present in searchData.
			// Therefore, we need to add it to selectedData to ensure Mantine can display it as a selected value.
			const updatedSelectedData = [
				...selectedData.filter((item) => newValue.includes(item.value)),
				...addedItems
					.map((id) => data.find((item) => item.value === id))
					.filter(Boolean),
			] as AnnouncementRelatedResourceItem[];

			setSelectedData(updatedSelectedData);
		},
		[data, selectedData, onChange, value]
	);

	const renderIcon = (item: AnnouncementRelatedResourceItem) => (
		<SecodaEntityIcon entity={item.entity} size={20} />
	);

	return (
		<Stack>
			<MultiSelect<AnnouncementRelatedResourceItem>
				clearable
				displayEntity
				searchValue={searchTerm}
				onSearchChange={setSearchTerm}
				renderIcon={renderIcon}
				size="md"
				sx={{
					'.mantine-MultiSelect-input': {
						maxHeight: 100,
						overflowY: 'auto',
					},
				}}
				value={value}
				setValue={controlledSetValue}
				searchable
				label="Related resources"
				data={data}
			/>
		</Stack>
	);
}
